import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { fmtDate, fmtCompact } from "../lib/datetime";
import { Link } from "react-router-dom";
import { BarChart3, ChevronLeft, ChevronRight, AlertTriangle, CalendarDays, CheckCircle2, Users2, FileCheck } from "lucide-react";

function mondayOf(d) {
  const date = new Date(d);
  const day = date.getDay();
  const diff = (day === 0 ? -6 : 1 - day);
  date.setDate(date.getDate() + diff);
  return date.toISOString().slice(0, 10);
}

export default function WeeklyReport() {
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/weekly-report`, { params: { week_start: weekStart } })
      .then((r) => setReport(r.data))
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail)));
  }, [weekStart]);

  const shift = (days) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + days);
    setWeekStart(mondayOf(d));
  };

  const completedThisWeek = useMemo(() => (report?.structures || []).filter((s) => s.max_percent >= 100), [report]);

  return (
    <div data-testid="weekly-report-page" style={{ padding: "24px 24px 64px" }}>
      <header style={{ marginBottom: 18 }}>
        <div className="mono" style={{ color: "#ff5a00", fontWeight: 600, fontSize: 12, letterSpacing: "0.1em" }}>SUPERVISOR · WEEKLY ROLL-UP · READ-ONLY AGGREGATE</div>
        <h1 style={{ fontSize: 28, fontWeight: 900, margin: "4px 0 0", display: "flex", alignItems: "center", gap: 10 }}>
          <BarChart3 size={26} color="#0f172a" /> Weekly report
        </h1>
        <div style={{ color: "#64748b", marginTop: 4 }}>Rolls up the daily logs for the selected week. Read-only — to amend, edit the underlying daily log.</div>
      </header>

      {error ? <div data-testid="weekly-error" className="card" style={{ padding: 12, marginBottom: 12, background: "#fef2f2", color: "#b91c1c", borderLeft: "4px solid #dc2626", display: "flex", gap: 8, alignItems: "center" }}><AlertTriangle size={16} /> {error}</div> : null}

      <div className="card" style={{ padding: 12, marginBottom: 16, display: "flex", gap: 10, alignItems: "center" }}>
        <button data-testid="weekly-prev" className="btn-ghost" onClick={() => shift(-7)}><ChevronLeft size={14} style={{ verticalAlign: "middle" }} />Prev</button>
        <div data-testid="weekly-window" style={{ fontWeight: 700, fontSize: 14, flex: 1, textAlign: "center" }}>
          <CalendarDays size={14} style={{ verticalAlign: -2, marginRight: 6 }} />
          Week of {report ? `${fmtDate(report.week_start)} → ${fmtDate(report.week_end)}` : "—"}
        </div>
        <button data-testid="weekly-next" className="btn-ghost" onClick={() => shift(7)}>Next<ChevronRight size={14} style={{ verticalAlign: "middle" }} /></button>
        <Link to="/daily-logs" data-testid="weekly-go-daily" className="btn-ghost" style={{ textDecoration: "none" }}>← Daily logs</Link>
      </div>

      {!report ? <div style={{ color: "#64748b" }}>Loading…</div> : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 24 }}>
            <Tile label="Daily logs filed" value={report.logs_count} testid="weekly-logs-count" accent="#0ea5e9" />
            <Tile label="Structures worked" value={(report.structures || []).length} testid="weekly-struct-count" accent="#3b82f6" />
            <Tile label="Completed this week" value={completedThisWeek.length} testid="weekly-completed-count" accent="#059669" />
            <Tile label="Handovers countersigned" value={(report.handovers_completed || []).length} testid="weekly-handovers-count" accent="#ff5a00" />
          </div>

          <h2 style={{ fontSize: 14, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#475569", margin: "0 0 12px" }}>
            <FileCheck size={14} style={{ verticalAlign: -2, marginRight: 6 }} />Per-structure progress
          </h2>
          <div className="card" style={{ padding: 0, overflow: "hidden", marginBottom: 24 }}>
            <table className="responsive-table" data-testid="weekly-structures-table">
              <thead><tr><th>Structure</th><th>Max % this week</th><th>Days worked</th><th>First seen</th><th>Last seen</th></tr></thead>
              <tbody>
                {(report.structures || []).length === 0 ? <tr><td colSpan={5} style={{ padding: 24, textAlign: "center", color: "#64748b" }}>No structures logged this week.</td></tr> :
                  report.structures.map((s) => (
                    <tr key={s.structure_id} data-testid={`weekly-struct-${s.structure_id}`}>
                      <td data-label="Structure" style={{ fontWeight: 600 }}>{s.structure_description}</td>
                      <td data-label="Max %"><PctBar pct={s.max_percent} /></td>
                      <td data-label="Days worked" className="mono">{(s.days_worked || []).length}</td>
                      <td data-label="First seen" className="mono" style={{ fontSize: 11 }}>{fmtDate(s.first_seen)}</td>
                      <td data-label="Last seen" className="mono" style={{ fontSize: 11 }}>{fmtDate(s.last_seen)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          <h2 style={{ fontSize: 14, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#475569", margin: "0 0 12px" }}>
            <Users2 size={14} style={{ verticalAlign: -2, marginRight: 6 }} />Gang-days
          </h2>
          <div className="card" style={{ padding: 0, overflow: "hidden", marginBottom: 24 }}>
            <table className="responsive-table" data-testid="weekly-gang-table">
              <thead><tr><th>Operative</th><th>Days on site</th><th>Total hours (logged)</th></tr></thead>
              <tbody>
                {(report.gang_days || []).length === 0 ? <tr><td colSpan={3} style={{ padding: 24, textAlign: "center", color: "#64748b" }}>No gang activity logged this week.</td></tr> :
                  report.gang_days.map((g) => (
                    <tr key={g.operative_id} data-testid={`weekly-gang-${g.operative_id}`}>
                      <td data-label="Operative" style={{ fontWeight: 600 }}>{g.operative_name || g.operative_id.slice(0, 8)}</td>
                      <td data-label="Days on site" className="mono">{g.days_count}</td>
                      <td data-label="Total hours" className="mono">{g.hours_total > 0 ? `${g.hours_total}h` : <span style={{ color: "#94a3b8" }}>—</span>}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          <h2 style={{ fontSize: 14, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#475569", margin: "0 0 12px" }}>
            <CheckCircle2 size={14} style={{ verticalAlign: -2, marginRight: 6 }} />Handovers countersigned
          </h2>
          <div className="card" style={{ padding: 0, overflow: "hidden", marginBottom: 24 }}>
            <table className="responsive-table" data-testid="weekly-handovers-table">
              <thead><tr><th>Cert ref</th><th>Structure</th><th>Countersigned at</th></tr></thead>
              <tbody>
                {(report.handovers_completed || []).length === 0 ? <tr><td colSpan={3} style={{ padding: 24, textAlign: "center", color: "#64748b" }}>No handovers countersigned this week.</td></tr> :
                  report.handovers_completed.map((h) => (
                    <tr key={h.cert_ref || h.structure_id} data-testid={`weekly-hand-${h.cert_ref}`}>
                      <td data-label="Cert ref" className="mono">{h.cert_ref}</td>
                      <td data-label="Structure" style={{ fontWeight: 600 }}>{h.structure_description || h.structure_id?.slice(0, 8)}</td>
                      <td data-label="Countersigned at" className="mono" style={{ fontSize: 11 }}>{fmtCompact(h.completed_at)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          <h2 style={{ fontSize: 14, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#475569", margin: "0 0 12px" }}>Source daily logs</h2>
          <div className="card" style={{ padding: 0, overflow: "hidden" }}>
            <table className="responsive-table" data-testid="weekly-source-logs">
              <thead><tr><th>Date</th><th>Supervisor</th><th>Structures</th><th>Gang</th><th>Status</th></tr></thead>
              <tbody>
                {(report.daily_summaries || []).length === 0 ? <tr><td colSpan={5} style={{ padding: 24, textAlign: "center", color: "#64748b" }}>No daily logs filed this week.</td></tr> :
                  report.daily_summaries.map((d) => (
                    <tr key={d.id} data-testid={`weekly-day-${d.id}`}>
                      <td data-label="Date" className="mono">{d.date}</td>
                      <td data-label="Supervisor">{d.supervisor_name || "—"}</td>
                      <td data-label="Structures" className="mono">{d.structures_count}</td>
                      <td data-label="Gang" className="mono">{d.gang_count}</td>
                      <td data-label="Status"><span className="mono" style={{ background: d.status === "confirmed" ? "#dcfce7" : "#fef3c7", color: d.status === "confirmed" ? "#065f46" : "#92400e", padding: "2px 8px", borderRadius: 3, fontSize: 10, fontWeight: 700, letterSpacing: "0.08em" }}>{(d.status || "").toUpperCase()}</span></td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function PctBar({ pct }) {
  const colour = pct >= 100 ? "#059669" : pct >= 50 ? "#0ea5e9" : pct > 0 ? "#f59e0b" : "#94a3b8";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div style={{ flex: 1, height: 8, background: "#f1f5f9", borderRadius: 4, overflow: "hidden", maxWidth: 160 }}>
        <div style={{ width: `${Math.min(100, pct)}%`, height: "100%", background: colour }} />
      </div>
      <span className="mono" style={{ fontSize: 11, fontWeight: 700, color: colour, minWidth: 36 }}>{pct}%</span>
    </div>
  );
}

function Tile({ label, value, accent, testid }) {
  return (
    <div data-testid={testid} className="card" style={{ padding: 16, borderTop: `3px solid ${accent || "#0f172a"}` }}>
      <div className="mono" style={{ fontSize: 11, color: "#64748b", letterSpacing: "0.1em", textTransform: "uppercase" }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 900, marginTop: 4 }}>{value}</div>
    </div>
  );
}
